import React, { useEffect, useMemo } from 'react';
import { injectIntl } from 'react-intl';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
} from 'react-bootstrap-table2-paginator';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
  headerSortingClasses,
} from '../../../../../_metronic/_helpers';
import { Pagination } from '../../../../../_metronic/_partials/controls';
import * as actions from '../../_redux/deliveries/deliveriesActions';
import { useDeliveriesUIContext } from './../DeliveriesUIContext';
import * as uiHelpers from './../DeliveriesUIHelpers';
import * as columnFormatters from './column-formatters';

const DeliveriesTable = (props) => {
  const { intl } = props;
  const deliverCodeLabel = intl.formatMessage({
    id: 'DELIVERIES.LABEL.DELIVERCODE',
  });
  const shipDateLabel = intl.formatMessage({
    id: 'DELIVERIES.LABEL.SHIPDATE',
  });
  const saleCountLabel = intl.formatMessage({
    id: 'DELIVERIES.LABEL.SALECOUNT',
  });
  const totalFeeLabel = intl.formatMessage({
    id: 'DELIVERIES.LABEL.TOTALFEE',
  });
  const deliveredDateLabel = intl.formatMessage({
    id: 'DELIVERIES.LABEL.DELIVEREDDATE',
  });
  const statusLabel = intl.formatMessage({ id: 'DELIVERIES.LABEL.STATUS' });
  const actionsLabel = intl.formatMessage({ id: 'DELIVERIES.LABEL.ACTIONS' });

  // Deliveries UI Context
  const DeliveriesUIContext = useDeliveriesUIContext();
  const DeliveriesUIProps = useMemo(() => {
    return {
      queryParams: DeliveriesUIContext.queryParams,
      setQueryParams: DeliveriesUIContext.setQueryParams,
      openViewDeliverDialog: DeliveriesUIContext.openViewDeliverDialog,
      openDeleteDeliverDialog: DeliveriesUIContext.openDeleteDeliverDialog,
    };
  }, [DeliveriesUIContext]);

  // Getting curret state of Deliveries list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.deliveries }),
    shallowEqual
  );
  //console.log('currentState:', currentState);
  const { totalCount, entities, listLoading } = currentState
    ? currentState
    : { totalCount: 0, entities: [], listLoading: false };

  // Deliveries Redux state
  const dispatch = useDispatch();
  useEffect(() => {
    // server call by queryParams
    dispatch(actions.fetchDeliveries(DeliveriesUIProps.queryParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DeliveriesUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: 'id',
      text: deliverCodeLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'shipDate',
      text: shipDateLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.DateColumnFormatter,
    },
    {
      dataField: 'boxedCount',
      text: saleCountLabel,
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: 'totalFee',
      text: totalFeeLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.PriceColumnFormatter,
    },
    {
      dataField: 'deliveredDate',
      text: deliveredDateLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      formatter: columnFormatters.DateColumnFormatter,
    },
    {
      dataField: 'status',
      text: statusLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
    },
    {
      dataField: 'action',
      text: actionsLabel,
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        openViewDeliverDialog: DeliveriesUIProps.openViewDeliverDialog,
        openDeleteDeliverDialog: DeliveriesUIProps.openDeleteDeliverDialog,
      },
      classes: 'text-right pr-0',
      headerClasses: 'text-right pr-3',
      style: {
        minWidth: '100px',
      },
    },
  ];
  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: DeliveriesUIProps.queryParams.pageSize,
    page: DeliveriesUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses='table-responsive'
                bordered={true}
                classes='table table-head-custom table-vertical-center overflow-hidden'
                bootstrap4
                remote
                keyField='id'
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  DeliveriesUIProps.setQueryParams
                )}
                useSortBy
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
};

export default injectIntl(DeliveriesTable);
